import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input, Option, Select } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal } from "@admin/components/atoms/Modal";
import { ModalProps } from "@admin/components/atoms/Modal";
import {
  AcceptenceCriteriaColors,
  addFermentedVegetableEnd,
  addFermentedVegetableStart,
  FermentedVegetableStart,
  FoodControlPointInstance,
  FoodManagementControls,
  InstanceStatus,
  updateFermentedVegetableEnd,
  updateFermentedVegetableStart,
} from "@enos-monorepo/lib";
import {
  END_PH_48_HOURS_OPTIONS,
  END_PH_OPTIONS,
  START_PH_OPTIONS,
} from "@enos-monorepo/lib/constants/ph";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface FermentedVegetableModalProps extends ModalProps {
  foodControlPointInstance: FoodControlPointInstance;
  type:
    | FoodManagementControls.vegetablespHbeginning
    | FoodManagementControls.vegetablespH48Hours
    | FoodManagementControls.vegetablespHfinal;
  initialData?: FermentedVegetableStart;
  callBack?: () => void;
}

export function FermentedVegetableModal({
  foodControlPointInstance,
  type,
  initialData,
  callBack,
  ...props
}: FermentedVegetableModalProps) {
  const [selectedPh, setSelectedPh] = useState<Option>();
  const [valid, setValid] = useState<boolean>(false);
  const phBeginning = useMemo(() => {
    return type === FoodManagementControls.vegetablespHbeginning;
  }, [type]);

  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const handleOnClose = () => {
    setSelectedPh(undefined);
    reset();
    props.onClose?.();
  };

  const fermentedVegetableSchema = useMemo(() => {
    return z.object({
      ph: z.string().refine((value) => value !== "0", {
        message: t("food.Ph value is required"),
      }),
      remark: z.string().optional(),
      correctiveActions: valid
        ? z.string().optional()
        : z.string().min(1, t("food.Corrective action is required")),
      saltContent: phBeginning
        ? z
            .string()
            .min(0)
            .refine(
              (data) => Number(data) > 0,

              {
                message: t("food.Salt content cannot be 0"),
              },
            )
        : z.string().optional(),
      storageTemperature: phBeginning
        ? z
            .string()
            .min(0)
            .max(100)
            .refine(
              (data) => Number(data) > 0,

              {
                message: t("food.Storage temperature cannot be 0"),
              },
            )
        : z.string().optional(),
    });
  }, [phBeginning, t, valid]);

  type FermentedVegetableSchema = z.infer<typeof fermentedVegetableSchema>;

  const options = useMemo(() => {
    if (phBeginning) {
      return START_PH_OPTIONS;
    }

    return type === FoodManagementControls.vegetablespH48Hours
      ? END_PH_48_HOURS_OPTIONS
      : END_PH_OPTIONS;
  }, [phBeginning, type]);

  const {
    formState: state,
    handleSubmit,
    register,
    setValue,
    reset,
  } = useForm<FermentedVegetableSchema>({
    resolver: zodResolver(fermentedVegetableSchema),
  });

  useEffect(() => {
    if (phBeginning) {
      setValid(true);
    } else {
      setValid(selectedPh?.color === AcceptenceCriteriaColors.Acceptable);
    }
  }, [phBeginning, selectedPh, initialData, options, setValue]);

  useEffect(() => {
    if (!initialData) {
      setValue("ph", "0");
    }
  }, [initialData, options, setValue]);

  const updateStartMutation = useMutation({
    mutationFn: updateFermentedVegetableStart,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance.companyLocationId,
        ],
      });
      handleOnClose();
      toast.success(t("food.Fermented Vegetable Start Updated Successfully"));
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("food.Failed to update Fermented Vegetable Start"));
    },
  });

  const updateEndMutation = useMutation({
    mutationFn: updateFermentedVegetableEnd,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance.companyLocationId,
        ],
      });
      handleOnClose();
      toast.success(t("food.Fermented Vegetable End Updated Successfully"));
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("food.Failed to update Fermented Vegetable End"));
    },
  });

  const startMutation = useMutation({
    mutationFn: addFermentedVegetableStart,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance.companyLocationId,
        ],
      });
      handleOnClose();
      toast.success(t("food.Heat Control Added Successfully"));
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("food.Failed to add Heat Control"));
    },
  });

  const endMutation = useMutation({
    mutationFn: addFermentedVegetableEnd,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance.companyLocationId,
        ],
      });
      handleOnClose();
      toast.success(t("food.Fermented Vegetable End Added Successfully"));
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("food.Failed to add Fermented Vegetable End"));
    },
  });

  useEffect(() => {
    if (initialData) {
      const { reason, remark } = initialData.note
        ? JSON.parse(initialData.note)
        : "";

      reset({
        remark,
        correctiveActions: reason,
        ph: initialData.ph?.toString(),
      });

      setValid(
        options.find((option) => option.value === initialData.ph)?.color ===
          AcceptenceCriteriaColors.Acceptable,
      );

      if (phBeginning) {
        setValue("saltContent", initialData.saltContent?.toString());
        setValue(
          "storageTemperature",
          initialData.storageTemperature?.toString(),
        );
      }
    }
  }, [initialData, options, phBeginning, reset, setValue]);

  const onSubmit = useCallback(
    (data: FermentedVegetableSchema) => {
      const basePayload = {
        companyId: foodControlPointInstance.companyId,
        companyLocationId: foodControlPointInstance.companyLocationId,
        reason: data.correctiveActions ?? "-",
        ph: Number(data.ph),
        remark: data.remark,
        status: !valid
          ? InstanceStatus.completedWithErrors
          : InstanceStatus.completed,
        controlPointInstanceId: foodControlPointInstance.id,
        foodControlPointInstance,
      };

      const startPayload = {
        ...basePayload,
        saltContent: Number(data.saltContent),
        storageTemperature: Number(data.storageTemperature),
      };

      if (phBeginning) {
        if (initialData) {
          updateStartMutation.mutate(startPayload);
        } else {
          startMutation.mutate(startPayload);
        }
      } else {
        if (initialData) {
          updateEndMutation.mutate(basePayload);
        } else {
          endMutation.mutate(basePayload);
        }
      }

      callBack?.();
    },

    [
      callBack,
      endMutation,
      foodControlPointInstance,
      initialData,
      phBeginning,
      startMutation,
      updateEndMutation,
      updateStartMutation,
      valid,
    ],
  );
  return (
    <Modal
      {...props}
      title={
        type === FoodManagementControls.vegetablespHbeginning
          ? t("food.Fermented Vegetable Start")
          : type === FoodManagementControls.vegetablespH48Hours
            ? t("food.Fermented Vegetable 48 Hours")
            : t("food.Fermented Vegetable End")
      }
      actionButtons={
        <>
          <Button variant="secondary" onClick={handleOnClose}>
            {t("common.Discard")}
          </Button>
          <Button onClick={handleSubmit(onSubmit)}>
            {t("food.Submit Entry")}
          </Button>
        </>
      }
    >
      <form>
        <div className="flex flex-col gap-2.5">
          <Label title={t("food.pH Measurement")} required>
            <Select
              options={options}
              {...register("ph")}
              defaultValue={initialData?.ph ?? "0"}
              onChange={(e) => {
                const option = options.find(
                  (option) => option.value === Number(e.target.value),
                );
                setSelectedPh(option);
                setValue("ph", e.target.value);
              }}
            />
          </Label>
          <ErrorMessage message={state.errors.ph?.message} />
        </div>
        {type === FoodManagementControls.vegetablespHbeginning && (
          <div>
            <div className="h-[1px] bg-gray-200 w-full my-2"></div>

            <div className="flex flex-col gap-2.5">
              <Label title={t("food.Salt Content")} required>
                <Input type="number" {...register("saltContent")} />
              </Label>
              <ErrorMessage message={state.errors.saltContent?.message} />
            </div>
            <div className="flex flex-col gap-2.5">
              <Label title={t("food.Storage Temperature")} required>
                <Input type="number" {...register("storageTemperature")} />
              </Label>
              <ErrorMessage
                message={state.errors.storageTemperature?.message}
              />
            </div>
            <div className="h-[1px] bg-gray-200 w-full my-2"></div>
          </div>
        )}

        {!valid && (
          <div className="flex flex-col gap-2.5">
            <Label title={t("food.Corrective Action")} required>
              <Input {...register("correctiveActions")} />
            </Label>
            <ErrorMessage message={state.errors.correctiveActions?.message} />
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          <Label title={t("food.Remarks")}>
            <Input {...register("remark")} />
          </Label>
          <ErrorMessage message={state.errors.remark?.message} />
        </div>
      </form>
    </Modal>
  );
}
